import { useState, useCallback, useEffect } from 'react';
import { Star, Trash2, Search, Filter, Users, Eye, EyeOff, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ratingAPI, serviceAPI } from '../../lib/api';
import { toast } from 'sonner';

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterServiceId, setFilterServiceId] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [reviewsRes, servicesRes] = await Promise.all([
        ratingAPI.getAllAdmin({ status: filterStatus !== 'all' ? filterStatus : undefined }),
        serviceAPI.getAll()
      ]);
      setReviews(reviewsRes.data);
      setServices(servicesRes.data);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    }
    setLoading(false);
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (window.confirm('এই রিভিউ মুছে ফেলতে চান?')) {
      try {
        await ratingAPI.delete(id);
        toast.success('রিভিউ মুছে ফেলা হয়েছে');
        fetchData();
      } catch (err) {
        toast.error('মুছতে সমস্যা হয়েছে');
      }
    }
  };

  const handleToggleVisibility = async (review) => {
    try {
      const res = await ratingAPI.toggleVisibility(review.id);
      toast.success(res.data.visible ? 'রিভিউ দৃশ্যমান করা হয়েছে' : 'রিভিউ লুকানো হয়েছে');
      fetchData();
    } catch (err) {
      toast.error('স্ট্যাটাস পরিবর্তন করতে সমস্যা হয়েছে');
    }
  };

  const handleUpdateStatus = async (reviewId, newStatus) => {
    try {
      await ratingAPI.updateStatus(reviewId, newStatus);
      const statusMessages = {
        approved: 'রিভিউ অনুমোদিত হয়েছে',
        rejected: 'রিভিউ প্রত্যাখ্যান করা হয়েছে',
        pending: 'রিভিউ পেন্ডিং করা হয়েছে'
      };
      toast.success(statusMessages[newStatus]);
      fetchData();
    } catch (err) {
      toast.error('স্ট্যাটাস আপডেট করতে সমস্যা হয়েছে');
    }
  };

  const renderStars = (rating) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={14} className={i <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'} />
      ))}
    </div>
  );

  const getStatusBadge = (status) => {
    const configs = {
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock, text: 'অপেক্ষমাণ' },
      approved: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle, text: 'অনুমোদিত' },
      rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle, text: 'প্রত্যাখ্যাত' }
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-xs flex items-center gap-1`}>
        <Icon size={12} />
        {config.text}
      </Badge>
    );
  };

  const filteredReviews = reviews.filter(r => {
    const matchesService = filterServiceId === 'all' || r.service_id === filterServiceId;
    const matchesSearch = searchQuery === '' ||
      r.reviewer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.review.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.service_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesService && matchesSearch;
  });

  const avgRating = filteredReviews.length > 0
    ? (filteredReviews.reduce((s, r) => s + r.rating, 0) / filteredReviews.length).toFixed(1)
    : '0.0';

  const ratingDistribution = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: filteredReviews.filter(r => r.rating === star).length,
    percent: filteredReviews.length > 0
      ? Math.round((filteredReviews.filter(r => r.rating === star).length / filteredReviews.length) * 100)
      : 0
  }));

  const pendingCount = filteredReviews.filter(r => r.status === 'pending').length;
  const approvedCount = filteredReviews.filter(r => r.status === 'approved').length;
  const rejectedCount = filteredReviews.filter(r => r.status === 'rejected').length;

  return (
    <div>
      <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6" data-testid="reviews-management-title">রিভিউ ম্যানেজমেন্ট</h1>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">মোট রিভিউ</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white" data-testid="total-reviews-count">{filteredReviews.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">গড় রেটিং</p>
          <div className="flex items-center gap-2">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{avgRating}</p>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} size={16} className={i <= Math.round(parseFloat(avgRating)) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'} />
              ))}
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">স্ট্যাটাস</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-bold text-yellow-600 flex items-center gap-1">
              <Clock size={14} /> {pendingCount}
            </span>
            <span className="text-lg font-bold text-green-600 flex items-center gap-1">
              <CheckCircle size={14} /> {approvedCount}
            </span>
            <span className="text-lg font-bold text-red-500 flex items-center gap-1">
              <XCircle size={14} /> {rejectedCount}
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">রেটিং বিতরণ</p>
          <div className="space-y-1">
            {ratingDistribution.map(d => (
              <div key={d.star} className="flex items-center gap-2 text-xs">
                <span className="w-3 text-gray-600 dark:text-gray-400">{d.star}</span>
                <Star size={10} className="fill-yellow-400 text-yellow-400" />
                <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-yellow-400 rounded-full" style={{ width: `${d.percent}%` }} />
                </div>
                <span className="w-6 text-right text-gray-500 dark:text-gray-400">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="রিভিউয়ার নাম, রিভিউ বা সেবাদাতা দিয়ে খুঁজুন..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="review-search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-40" data-testid="review-filter-status">
              <SelectValue placeholder="স্ট্যাটাস" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">সব স্ট্যাটাস</SelectItem>
              <SelectItem value="pending">অপেক্ষমাণ</SelectItem>
              <SelectItem value="approved">অনুমোদিত</SelectItem>
              <SelectItem value="rejected">প্রত্যাখ্যাত</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterServiceId} onValueChange={setFilterServiceId}>
            <SelectTrigger className="w-full sm:w-56" data-testid="review-filter-service">
              <Filter size={14} className="mr-1" />
              <SelectValue placeholder="সেবাদাতা ফিল্টার" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">সব সেবাদাতা</SelectItem>
              {services.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />)}
        </div>
      ) : filteredReviews.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <Star className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600 dark:text-gray-400">কোনো রিভিউ পাওয়া যায়নি</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredReviews.map((review) => (
            <div
              key={review.id}
              className={`bg-white dark:bg-gray-800 rounded-xl p-4 border transition-colors ${
                review.status === 'rejected'
                  ? 'border-red-200 dark:border-red-800/50 opacity-60'
                  : review.status === 'pending'
                  ? 'border-yellow-200 dark:border-yellow-800/50'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
              data-testid={`review-card-${review.id}`}
            >
              <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <div className="w-8 h-8 rounded-full bg-[#2E7D32]/10 flex items-center justify-center">
                        <span className="text-[#2E7D32] font-bold text-sm">{review.reviewer_name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{review.reviewer_name}</p>
                        {review.reviewer_email && <p className="text-xs text-gray-400">{review.reviewer_email}</p>}
                      </div>
                      {renderStars(review.rating)}
                      {getStatusBadge(review.status)}
                    </div>
                    {review.review && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg mt-2">{review.review}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="outline" className="text-xs dark:border-gray-600 dark:text-gray-300">
                        <Users size={10} className="mr-1" />
                        {review.service_name}
                      </Badge>
                      <span className="text-xs text-gray-400">
                        {new Date(review.created_at).toLocaleDateString('bn-BD')}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  {review.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleUpdateStatus(review.id, 'approved')}
                        className="bg-green-600 hover:bg-green-700 text-white text-xs"
                        data-testid={`approve-review-${review.id}`}
                      >
                        <CheckCircle size={14} className="mr-1" />
                        অনুমোদন
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleUpdateStatus(review.id, 'rejected')}
                        className="text-xs"
                        data-testid={`reject-review-${review.id}`}
                      >
                        <XCircle size={14} className="mr-1" />
                        প্রত্যাখ্যান
                      </Button>
                    </>
                  )}
                  {review.status === 'approved' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUpdateStatus(review.id, 'rejected')}
                      className="text-red-500 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs"
                    >
                      <XCircle size={14} className="mr-1" />
                      প্রত্যাখ্যান
                    </Button>
                  )}
                  {review.status === 'rejected' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUpdateStatus(review.id, 'approved')}
                      className="text-green-600 border-green-200 hover:bg-green-50 dark:hover:bg-green-900/20 text-xs"
                    >
                      <CheckCircle size={14} className="mr-1" />
                      অনুমোদন
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleVisibility(review)}
                    className={review.visible === false ? 'text-red-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' : 'text-green-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'}
                    title={review.visible === false ? 'দৃশ্যমান করুন' : 'লুকান'}
                    data-testid={`visibility-toggle-${review.id}`}
                  >
                    {review.visible === false ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(review.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    data-testid={`delete-review-${review.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsManagement;
